/**
 * Document window frame — the shared window chrome around one document's
 * surface: the window container (active/inactive presentation), the floating
 * title bar (dock as tab / maximize / close), and the 8-way resize grips.
 * Ported from pik's CanvasWindowCore chrome; the document BODY is the app's
 * surface, rendered through `renderSurface`.
 */

import { memo, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import type {
  DocTab,
  DocumentWorkspace,
  ResizeDirection,
  SurfaceRenderContext,
} from '../types';
import type { WorkspaceInteractions } from './useWorkspaceInteractions';
import { emitGeometryChanged } from './protocol';
import { CloseIcon, MaximizeIcon, PanelTopIcon } from './icons';

const RESIZE_DIRECTIONS: ResizeDirection[] = [
  'n',
  's',
  'e',
  'w',
  'ne',
  'nw',
  'se',
  'sw',
];

export interface DocumentWindowFrameProps {
  tab: DocTab;
  /** Creation-order index (cascade offsets, inactive stacking) */
  index: number;
  isActive: boolean;
  /** When false the window ignores pointer events (e.g. overlapped in cascade) */
  interactive: boolean;
  /** True when the window is torn out and floats freely; shows the title bar */
  floating: boolean;
  /** Runtime window geometry (tile cell, cascade offset, floating rect) */
  style?: CSSProperties;
  workspace: DocumentWorkspace;
  renderSurface: (ctx: SurfaceRenderContext) => React.ReactNode;
  onActivate: (id: string) => void;
  /** Close request — routed to the app (its dirty guard owns the decision) */
  onCloseRequest: (id: string) => void;
  interactions: WorkspaceInteractions;
}

const DocumentWindowFrameImpl = ({
  tab,
  index,
  isActive,
  interactive,
  floating,
  style,
  workspace,
  renderSurface,
  onActivate,
  onCloseRequest,
  interactions,
}: DocumentWindowFrameProps) => {
  const { classNames: cn, labels } = workspace;

  // Floating-window edge resize: drags mutate the window element directly and
  // commit the final geometry once on release (no per-move store writes)
  const applyWindowResize = (
    d: {
      dir: ResizeDirection;
      startX: number;
      startY: number;
      rect: DOMRect;
      areaRect: DOMRect;
      el: HTMLElement;
    },
    clientX: number,
    clientY: number,
  ) => {
    const MIN_W = 280;
    const MIN_H = 160;
    const dx = clientX - d.startX;
    const dy = clientY - d.startY;
    let left = d.rect.left - d.areaRect.left;
    let top = d.rect.top - d.areaRect.top;
    let width = d.rect.width;
    let height = d.rect.height;
    if (d.dir.includes('e'))
      width = Math.max(
        MIN_W,
        Math.min(width + dx, d.areaRect.width - left),
      );
    if (d.dir.includes('s'))
      height = Math.max(
        MIN_H,
        Math.min(height + dy, d.areaRect.height - top),
      );
    if (d.dir.includes('w')) {
      const applied = Math.max(-left, Math.min(dx, width - MIN_W));
      left += applied;
      width -= applied;
    }
    if (d.dir.includes('n')) {
      const applied = Math.max(-top, Math.min(dy, height - MIN_H));
      top += applied;
      height -= applied;
    }
    d.el.style.left = `${left}px`;
    d.el.style.top = `${top}px`;
    d.el.style.width = `${width}px`;
    d.el.style.height = `${height}px`;
  };

  const handleResizePointerDown =
    (dir: ResizeDirection) =>
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || !floating) return;
      const el = event.currentTarget.closest(
        '[data-doc-id]',
      ) as HTMLElement | null;
      const areaEl = el?.parentElement;
      if (!el || !areaEl) return;
      event.preventDefault();
      event.stopPropagation();
      const drag = {
        dir,
        startX: event.clientX,
        startY: event.clientY,
        rect: el.getBoundingClientRect(),
        areaRect: areaEl.getBoundingClientRect(),
        el,
      };
      // Normalize percentage-based geometry ('100%' maximize) to pixels first
      applyWindowResize(drag, event.clientX, event.clientY);

      const commit = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleCancel);
        const position = {
          x: parseFloat(el.style.left) || 0,
          y: parseFloat(el.style.top) || 0,
        };
        const size = {
          width: el.offsetWidth,
          height: el.offsetHeight,
        };
        workspace.store.getState().setWindowState(tab.id, {
          position,
          size,
          dockZone: undefined,
        });
        emitGeometryChanged(tab.id);
      };
      const handleMove = (ev: PointerEvent) => {
        // Button released outside the webview: commit what we have
        if (ev.buttons === 0) {
          commit();
          return;
        }
        applyWindowResize(drag, ev.clientX, ev.clientY);
      };
      const handleUp = () => commit();
      const handleCancel = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleCancel);
      };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleCancel);
    };

  return (
    <div
      data-doc-id={tab.id}
      className={`${cn.window}${isActive ? '' : ` ${cn.windowInactive}`}`}
      style={{
        ...style,
        pointerEvents: interactive ? undefined : 'none',
      }}
      onMouseDown={(event) => {
        if (event.button === 0) onActivate(tab.id);
      }}
    >
      {floating && (
        <div
          className={cn.windowTitleBar}
          onPointerDown={(event) => {
            if ((event.target as HTMLElement).closest('button')) return;
            interactions.handleWindowHeaderPointerDown(tab.id, event);
          }}
          onDoubleClick={() => interactions.toggleWindowMaximize(tab.id)}
        >
          <span className={cn.windowTitle}>{tab.label}</span>
          <div className={cn.windowTitleControls}>
            <button
              className={cn.windowTitleButton}
              onClick={() => interactions.dockWindow(tab.id)}
              aria-label={labels.dockAsTabAria(tab.label)}
              title={labels.dockAsTab(tab.label)}
            >
              <PanelTopIcon />
            </button>
            <button
              className={cn.windowTitleButton}
              onClick={() => interactions.toggleWindowMaximize(tab.id)}
              aria-label={labels.maximizeAria(tab.label)}
            >
              <MaximizeIcon />
            </button>
            <button
              className={cn.windowTitleButton}
              onClick={() => onCloseRequest(tab.id)}
              aria-label={labels.closeWindowAria(tab.label)}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      )}
      {renderSurface({ tab, index, isActive, interactive, floating })}
      {floating &&
        RESIZE_DIRECTIONS.map((dir) => (
          <div
            key={dir}
            className={workspace.classNames.resizeHandle?.(dir)}
            onPointerDown={handleResizePointerDown(dir)}
          />
        ))}
    </div>
  );
};

/**
 * Memoized like pik's CanvasWindow: a frame re-renders only when its own
 * geometry or presentation actually changes. The `interactions` bundle is a
 * fresh object per shell render but every FIELD is a stable useCallback, so
 * the comparator skips it.
 */
export const DocumentWindowFrame = memo(
  DocumentWindowFrameImpl,
  (prev, next) =>
    prev.tab === next.tab &&
    prev.index === next.index &&
    prev.isActive === next.isActive &&
    prev.interactive === next.interactive &&
    prev.floating === next.floating &&
    prev.style === next.style &&
    prev.workspace === next.workspace &&
    prev.renderSurface === next.renderSurface &&
    prev.onActivate === next.onActivate &&
    prev.onCloseRequest === next.onCloseRequest,
);
